import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Lightbox from './Lightbox';
import useCarousel from '../hooks/useCarousel';

const SLIDE_VARIANTS = {
  enter: (direction) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? 300 : -300,
    opacity: 0
  })
};

const SLIDE_TRANSITION = {
  x: { type: "spring", stiffness: 300, damping: 30 },
  opacity: { duration: 0.2 }
};

const CarouselArrows = ({ onPrev, onNext, alwaysVisible }) => {
  const visibility = alwaysVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';
  const baseClass = `absolute top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white flex items-center justify-center transition-opacity duration-300 z-10 ${visibility}`;

  return (
    <>
      <button onClick={onPrev} className={`${baseClass} left-3`} aria-label="Previous image" data-testid="carousel-prev">
        <ChevronLeft size={20} className="text-black" />
      </button>
      <button onClick={onNext} className={`${baseClass} right-3`} aria-label="Next image" data-testid="carousel-next">
        <ChevronRight size={20} className="text-black" />
      </button>
    </>
  );
};

const CarouselDots = ({ images, currentIndex, onSelect }) => (
  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
    {images.map((image, index) => (
      <button
        key={image}
        onClick={() => onSelect(index)}
        data-testid={`carousel-dot-${index}`}
        className={`w-2 h-2 rounded-full transition-all duration-300 ${
          index === currentIndex ? 'bg-white w-4' : 'bg-white/50 hover:bg-white/80'
        }`}
        aria-label={`Go to image ${index + 1}`}
      />
    ))}
  </div>
);

const SingleImage = ({ image, alt, className }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <>
      <div 
        className={`relative overflow-hidden ${className} cursor-pointer`}
        onClick={() => setLightboxOpen(true)}
      >
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
      <Lightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        image={image}
      />
    </>
  );
};

const ImageCarousel = ({ images, alt, className = "aspect-[4/3]", objectFit = "cover", showArrows = false, scales = [] }) => {
  const carousel = useCarousel(images?.length || 0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!images || images.length === 0) return null;
  if (images.length === 1) {
    return <SingleImage image={images[0]} alt={alt} className={className} />;
  }

  const { currentIndex, direction, paginate, goToIndex, handleDragEnd } = carousel;

  return (
    <>
      <div className={`relative overflow-hidden ${className} group`}>
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={SLIDE_TRANSITION}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={handleDragEnd}
            onClick={() => setLightboxOpen(true)}
            className="absolute w-full h-full cursor-pointer flex items-center justify-center"
          >
            <img
              src={images[currentIndex]}
              alt={`${alt} ${currentIndex + 1}`}
              loading="lazy"
              className={`w-full h-full ${objectFit === "contain" ? "object-contain" : "object-cover"}`}
              style={scales[currentIndex] ? { transform: `scale(${scales[currentIndex]})` } : {}}
            />
          </motion.div>
        </AnimatePresence>

        <CarouselArrows 
          onPrev={() => paginate(-1)} 
          onNext={() => paginate(1)} 
          alwaysVisible={showArrows} 
        />
        <CarouselDots 
          images={images} 
          currentIndex={currentIndex} 
          onSelect={goToIndex} 
        />
      </div>

      <Lightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        image={images[currentIndex]}
        onPrev={() => paginate(-1)}
        onNext={() => paginate(1)}
        hasPrev={images.length > 1}
        hasNext={images.length > 1}
      />
    </>
  );
};

export default ImageCarousel;
